import React, { useState, useRef, useEffect, useCallback } from "react";
import { Drawer, Box, Typography, IconButton, Divider } from "@mui/material";
import { X } from "lucide-react";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import RatingCard from "@/modules/lead-page/new/components/ratings/rating-card/rating-card";
import AddComment from "@/modules/lead-page/new/components/ratings/rating-card/add-comment";
import { useAddRatingComment } from "@/modules/lead-page/new/hooks/ratings/add-rating-comment.hook";
import { getRatingActions } from "@/modules/lead-page/new/components/ratings/rating-card/rating-actions";
import { isFollowUpResolved } from "@/modules/lead-page/utils";
import ResolveRatingModal from "@/modules/lead-page/new/components/ratings/rating-card/resolve-rating-modal";
import RatingDropdown from "@/modules/lead-page/new/components/ratings/rating-thread/rating-dropdown";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import RatingThreadSkeleton from "@/modules/lead-page/new/components/ratings/rating-thread/rating-thread-skeleton";
import { RatingThreadDrawerProps } from "@/modules/lead-page/types";

const RatingThreadDrawer: React.FC<RatingThreadDrawerProps> = ({
  isOpen,
  onClose,
  ratingUId,
  agent,
  rating,
  comment,
  touchpoint,
  followUpStatus,
  createdAt,
  comments,
  findInActivity,
  onResolvedFollowUp,
  isLoading = false,
}) => {
  const { isMobile } = useScreenSize();

  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const previousCommentsLengthRef = useRef<number>(comments?.length || 0);

  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      setTimeout(() => {
        scrollContainerRef.current?.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    }
  }, []);

  const { handleSendComment, isPending } = useAddRatingComment({
    ratingUId,
    onSuccess: scrollToBottom,
  });

  // Scroll to bottom when comments are added
  useEffect(() => {
    if (
      comments &&
      comments.length > previousCommentsLengthRef.current &&
      scrollContainerRef.current
    ) {
      scrollToBottom();
    }
    previousCommentsLengthRef.current = comments?.length || 0;
  }, [comments, scrollToBottom]);

  const [dropdownCloseHandler, setDropdownCloseHandler] = useState<
    (() => void) | null
  >(null);

  const ratingActions = React.useMemo(() => {
    return getRatingActions({
      findInActivity,
      onResolvedFollowUp,
      onResolveClick: () => setIsResolveModalOpen(true),
      isResolved: isFollowUpResolved(followUpStatus),
      onActionClick: dropdownCloseHandler || undefined,
    });
  }, [
    findInActivity,
    onResolvedFollowUp,
    followUpStatus,
    dropdownCloseHandler,
  ]);

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      sx={{
        zIndex: 1400,
        "& .MuiDrawer-paper": {
          width: isMobile ? "100%" : "460px",
          backgroundColor: nawyTheme.nawy.colors.primitives.white[100],
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          zIndex: 1400,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: `${nawyTheme.nawy.spacing.xs} ${nawyTheme.nawy.spacing.md} ${nawyTheme.nawy.spacing.xs} ${nawyTheme.nawy.spacing.md}`,
          borderBottom: `1px solid ${nawyTheme.nawy.colors.semantic.border.secondary}`,
        }}
      >
        <Typography
          sx={{
            fontWeight:
              nawyTheme.nawy.typography.english.variants.headline.xs.fontWeight,
            color: nawyTheme.nawy.colors.semantic.text.primary,
          }}
        >
          Rating thread
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <RatingDropdown
            actions={ratingActions}
            onActionClick={(closeHandler) =>
              setDropdownCloseHandler(() => closeHandler)
            }
            popperSx={{ zIndex: 1550 }}
          />
          <IconButton
            onClick={onClose}
            sx={{
              color: nawyTheme.nawy.colors.semantic.text.primary,
              "&:hover": {
                backgroundColor: nawyTheme.nawy.colors.semantic.layer.secondary,
              },
            }}
          >
            <X size={nawyTheme.nawy.spacing.md} />
          </IconButton>
        </Box>
      </Box>

      <Divider />

      {/* Content */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          width: isMobile ? "100%" : "460px",
        }}
      >
        {isLoading ? (
          <RatingThreadSkeleton />
        ) : (
          <RatingCard
            ratingUId={ratingUId}
            agent={agent}
            rating={rating}
            comment={comment}
            touchpoint={touchpoint}
            followUpStatus={followUpStatus}
            createdAt={createdAt}
            comments={comments}
            findInActivity={findInActivity}
            onResolvedFollowUp={onResolvedFollowUp}
            isInThread={true}
          />
        )}
      </Box>

      {/* Footer - Add Comment */}
      <Box
        sx={{
          padding: `${nawyTheme.nawy.spacing.sm} ${nawyTheme.nawy.spacing.sm}`,
        }}
      >
        <AddComment
          handleSendComment={handleSendComment}
          isPending={isPending}
        />
      </Box>

      {/* Resolve Rating Modal */}
      {onResolvedFollowUp && (
        <ResolveRatingModal
          open={isResolveModalOpen}
          setOpen={setIsResolveModalOpen}
          ratingUId={ratingUId}
          onSuccess={() => {
            onResolvedFollowUp();
          }}
          modalSx={{ zIndex: 1600 }}
        />
      )}
    </Drawer>
  );
};

export default RatingThreadDrawer;
